import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BeachSelector from "@/components/BeachSelector";
import WeatherCard from "@/components/WeatherCard";
import SurfReport from "@/components/SurfReport";
import TideChart from "@/components/TideChart";
import AlertsPanel, { useAlertCount } from "@/components/AlertsPanel";
import ChatBox from "@/components/ChatBox";
import WeatherRadar from "@/components/WeatherRadar";
import FeaturedVideo from "@/components/FeaturedVideo";
import PopoyoPromoStrip from "@/components/PopoyoPromoStrip";
import HistoryTeaser from "@/components/HistoryTeaser";
import AdminPanel from "@/components/AdminPanel";
import ShareButton from "@/components/ShareButton";
import Typewriter from "@/components/Typewriter";
import { useSEO } from "@/hooks/useSEO";
import { Loader2 } from "lucide-react";

// Below-the-fold sections are code-split + mounted only after first paint so
// the hero, radar, and live data render with less JS to parse upfront.
const SurfSessions = lazy(() => import("@/components/SurfSessions"));
const SurfPlans = lazy(() => import("@/components/SurfPlans"));
const EventsSection = lazy(() => import("@/components/EventsSection"));
const NonProfits = lazy(() => import("@/components/NonProfits"));
const NicaraguaTaxiCTA = lazy(() => import("@/components/NicaraguaTaxiCTA"));
const SuggestionsSection = lazy(() => import("@/components/SuggestionsSection"));
const EmergencyContacts = lazy(() => import("@/components/EmergencyContacts"));
const LocalPartners = lazy(() => import("@/components/LocalPartners"));

function SectionSkeleton({ minHeight = 180 }) {
  return (
    <div
      className="clay p-6 animate-pulse bg-[color:var(--sand-2)]/60"
      style={{ minHeight }}
      aria-hidden
    />
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1691520507686-6ffb28b022a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Home() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [beachId, setBeachId] = useState("san-juan-del-sur");
  const { open: openAuth } = useAuthDialog();
  const queryClient = useQueryClient();
  const prefsAppliedRef = useRef(false);

  // Apply saved preferences once after login
  useEffect(() => {
    if (!user || !user.id || prefsAppliedRef.current) return;
    prefsAppliedRef.current = true;
    if (user.preferred_beach_id) queueMicrotask(() => setBeachId(user.preferred_beach_id));
    if (user.preferred_lang && user.preferred_lang !== lang) queueMicrotask(() => setLang(user.preferred_lang));
  }, [user, lang, setLang]);

  // Persist beach changes for logged-in users (skip the initial apply)
  useEffect(() => {
    if (!user || !user.id) return;
    if (!prefsAppliedRef.current) return;
    if (user.preferred_beach_id === beachId) return;
    const handle = setTimeout(() => {
      http.patch("/auth/me", { preferred_beach_id: beachId }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [beachId, user]);

  // Persist lang changes for logged-in users
  useEffect(() => {
    if (!user || !user.id) return;
    if (!prefsAppliedRef.current) return;
    if (user.preferred_lang === lang) return;
    const handle = setTimeout(() => {
      http.patch("/auth/me", { preferred_lang: lang }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [lang, user]);

  const beachesQ = useQuery({
    queryKey: ["beaches"],
    queryFn: async () => (await http.get("/beaches")).data,
  });

  const surfQ = useQuery({
    queryKey: ["surf", beachId],
    queryFn: async () => (await http.get(`/surf/${beachId}`, { params: { days: 7 } })).data,
    enabled: !!beachId,
    refetchInterval: 15 * 60_000,
    placeholderData: keepPreviousData,
  });

  const alertCount = useAlertCount();

  // Per-route SEO + share metadata
  useSEO({
    title: "San Juan del Sur, Nicaragua · Live Surf, Tides, Weather & Community — SJDS.app",
    description:
      "Everything you need for San Juan del Sur, Nicaragua: live surf reports, tide charts, weather radar, earthquake & tsunami alerts, moon calendar, community chat, event calendar, local business directory and the complete history of San Juan del Sur.",
    path: "/",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--sand)]">
      <Header alertCount={alertCount} />

      {/* HERO */}
      <section
        data-testid="hero"
        className="relative w-full overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center 40%",
          minHeight: 320,
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(27,59,111,0.35) 0%, rgba(27,59,111,0.55) 60%, rgba(253,251,247,0.95) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 flex items-start gap-6">
          <img
            src="/brand/logo.png"
            alt="SJDS.app logo"
            data-testid="hero-logo"
            className="hidden md:block w-36 h-36 lg:w-44 lg:h-44 rounded-full shadow-2xl object-cover shrink-0"
            style={{ background: "#FDFBF7", boxShadow: "0 20px 50px -20px rgba(0,0,0,0.45), 0 0 0 4px rgba(255,255,255,0.3)" }}
          />
          <div>
            <p className="eyebrow !text-white mb-3" style={{ color: "#ffffff", textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}>SanJuanDelSur.app · SJDS.app</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl"
              style={{ fontFamily: "Outfit", textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
              data-testid="hero-headline"
            >
              <Typewriter text={t("app_tagline")} speed={32} startDelay={220} testId="hero-typewriter" />
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/95 max-w-2xl" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.4)" }}>
              {t("app_subtagline")}
            </p>
            <div className="mt-5">
              <ShareButton
                path="/"
                title="SJDS.app — San Juan del Sur Surf, Tides & Community"
                text="Live radar, surf reports, push alerts and a community chat for San Juan del Sur, Nicaragua."
                testId="share-home-btn"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="wave-divider" />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {/* ADMIN DASHBOARD — only renders for users with role=admin (returns null otherwise) */}
        <div id="admin" className="mb-6 scroll-mt-24">
          <AdminPanel />
        </div>

        {/* Beach selector */}
        <div className="clay p-5 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="beach-selector-wrap">
          <BeachSelector
            beaches={beachesQ.data || []}
            beachId={beachId}
            onChange={setBeachId}
          />
          <p className="text-xs text-[color:var(--ink-2)] hidden md:block">
            {t("saved_to_phone")}
          </p>
        </div>

        {/* FEATURED SURF VIDEO — eye-catching intro right above the Windy radar */}
        <FeaturedVideo />

        {/* Sister-site promo — nudge Popoyo/Tola visitors over to Popoyo.app */}
        <PopoyoPromoStrip />

        {/* LIVE WEATHER RADAR — top of page so people land on the storm view */}
        <div id="weather" className="mb-6 scroll-mt-24">
          <WeatherRadar beach={(beachesQ.data || []).find((b) => b.id === beachId)} />
        </div>

        {/* HISTORY TEASER — sits directly under the live radar */}
        <div className="mb-6">
          <HistoryTeaser />
        </div>

        {/* SURF + WEATHER DATA GRID — surf report, current conditions, tides, alerts */}
        {surfQ.isLoading && (
          <div className="flex items-center justify-center py-12 text-[color:var(--ink-2)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("loading_surf")}
          </div>
        )}
        {surfQ.data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-6 mb-6">
            <div className="lg:col-span-3">
              <SurfReport
                data={surfQ.data}
                onNoteSaved={() => queryClient.invalidateQueries({ queryKey: ["surf", beachId] })}
              />
            </div>
            <div className="lg:col-span-3">
              <WeatherCard data={surfQ.data} />
            </div>
            <div className="lg:col-span-6">
              <TideChart tide={surfQ.data.tide} moon={surfQ.data.moon} />
            </div>
            <div className="lg:col-span-6">
              <AlertsPanel />
            </div>
          </div>
        )}

        {/* CHAT — community general chat */}
        <div id="community" className="mb-6 scroll-mt-24">
          <ChatBox onOpenAuth={() => openAuth("login")} />
        </div>

        {/* SURF SESSIONS — community meet-up calendar */}
        <div id="surf" className="mb-6 scroll-mt-24">
          <Suspense fallback={<SectionSkeleton minHeight={320} />}>
            <SurfSessions
              beaches={beachesQ.data || []}
              onOpenAuth={() => openAuth("login")}
            />
          </Suspense>
        </div>

        {/* COMMUNITY EVENTS — surf contests, yoga, moon parties, live music */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={280} />}>
            <EventsSection />
          </Suspense>
        </div>

        {/* PERSONAL SURF PLANS + FRIEND INVITES */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={260} />}>
            <SurfPlans
              beaches={beachesQ.data || []}
              onOpenAuth={() => openAuth("login")}
            />
          </Suspense>
        </div>

        {/* HELP THE COMMUNITY — local non-profits & donation links */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={220} />}>
            <NonProfits />
          </Suspense>
        </div>

        {/* SUGGESTIONS — community → admin feature requests */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={200} />}>
            <SuggestionsSection />
          </Suspense>
        </div>

        {/* NICARAGUA TAXI — sponsored transportation CTA, placed right above emergency */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={140} />}>
            <NicaraguaTaxiCTA />
          </Suspense>
        </div>

        {/* EMERGENCY CONTACTS — final block before partners + footer */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={260} />}>
            <EmergencyContacts />
          </Suspense>
        </div>

        {/* LOCAL PARTNERS — paid websites Greg has built for SJDS businesses */}
        <div id="partners" className="mb-6 scroll-mt-24">
          <Suspense fallback={<SectionSkeleton minHeight={320} />}>
            <LocalPartners />
          </Suspense>
        </div>
      </main>

      <Footer />
    </div>
  );
}
